import { useCallback, useReducer, useContext, useEffect } from 'react';
import axios from 'axios';
import { apiExpress, options } from 'lib/config';
import { ActionDispatchContext } from 'lib/ActionDispatchContext';

import {
  ActionType,
  requestActionCreator,
  successActionCreator,
  failureActionCreator,
} from '../actionCreators';
import { FreemiumTemplateId } from 'lib/const/VideoTemplateItems';
import { checkIfFeatureIsEnabled } from 'lib/utils/productFeature';
import { ICombinedUserData } from 'lib/context';
import { productFeature } from 'lib/utils/productFeature';

export type VideoShareLinkData = {
  url: string;
  html: string;
  key?: string;
};

export type VideoShareSetData = {
  videoId: string;
  templateId: number;
  linksetId?: string;
  overlayId?: string;
  vin: string;
  allowVideoReply?: boolean;
  allowReactions?: boolean;
};

interface VideoShareLinkStore {
  url: VideoShareLinkData;
  data: VideoShareSetData;
  loading: boolean;
  error: string | null;
  setData: (VideoShareSetData: VideoShareSetData) => void;
  invalidate(): void;
}

interface VideoShareLinkStoreState {
  url: VideoShareLinkData | undefined;
  data: VideoShareSetData | undefined;
  error: string | null;
  loading: boolean;
  isValid: boolean;
  videoId?: number;
}

interface FetchParams {
  dispatch: React.Dispatch<ActionType>;
  data: any;
  videoId: string;
}

function reducer(state: VideoShareLinkStoreState, action: ActionType) {
  switch (action.type) {
    case 'INVALIDATE':
      return {
        ...state,
        isValid: false,
      };

    case 'SET_DATA':
      return {
        ...state,
        data: action.payload,
        videoId: action.payload.videoId,
        isValid: false,
      };

    case 'FETCH_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        url: action.payload,
        loading: false,
        error: null,
        isValid: true,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        data: undefined,
        loading: false,
        error: action.payload,
        isValid: true,
      };

    default:
      return state;
  }
}

const useActionListener = (dispatch: Function) => {
  const { action } = useContext(ActionDispatchContext);

  useEffect(() => {
    dispatch(action);
  }, [action, dispatch]);
};

export const getExpressQuickShareLink = async (
  user: ICombinedUserData,
  videoId: string,
  vin?: string,
  useAutomotive = false,
  isFreeUser: boolean = false
) => {
  const exitLinksEnabled = checkIfFeatureIsEnabled(user, productFeature.CTAS);
  const websiteOverlaysEnabled = checkIfFeatureIsEnabled(
    user,
    productFeature.WEBSITE_OVERLAY
  );
  const promiseTemplates = isFreeUser
    ? Promise.resolve()
    : axios
        .get(`${apiExpress}templates`, {
          headers: options.bearerToken,
        })
        .catch(err => err);
  const promiseExitLinksets = !exitLinksEnabled
    ? Promise.resolve()
    : axios
        .get(`${apiExpress}exitlinksets`, {
          headers: options.bearerToken,
        })
        .catch(err => err);
  const promiseOverlays = !websiteOverlaysEnabled
    ? Promise.resolve()
    : axios
        .get(`${apiExpress}overlays`, {
          headers: options.bearerToken,
        })
        .catch(err => err);
  let params: {
    vin: string;
    templateId: number;
    linksetId: number;
    overlayId: number;
    useAutomotive?: boolean;
  } = {
    vin: vin || '',
    templateId: 0,
    linksetId: 0,
    overlayId: 0,
  };
  if (useAutomotive) {
    params = { ...params, useAutomotive };
  }
  await Promise.all([
    promiseTemplates,
    promiseExitLinksets,
    promiseOverlays,
  ]).then(([responseTemplates, responseExitLinksets, responseOverlays]) => {
    if (responseTemplates instanceof Error) {
      throw new Error('Error retrieving templates!');
    }
    if (responseExitLinksets instanceof Error) {
      throw new Error('Error retrieving linksets!');
    }
    if (responseOverlays instanceof Error) {
      throw new Error('Error retrieving websites!');
    }
    let templateId = 0;
    if (isFreeUser) {
      templateId = FreemiumTemplateId;
    } else {
      const { data: templatesData } = responseTemplates;
      if (templatesData.default) {
        templateId = templatesData.default.id;
      }
    }
    let linksetId = 0;
    if (exitLinksEnabled) {
      const { linksets } = responseExitLinksets.data;
      const defaultLinkset = linksets.find((link: any) => !!link.defaultLinks);
      if (defaultLinkset) {
        linksetId = defaultLinkset.id;
      }
    }

    let overlayId = 0;
    if (websiteOverlaysEnabled) {
      const { data: websitesData }: any = responseOverlays;

      if (websitesData && websitesData.default) {
        overlayId = websitesData.default.id;
      }
    }

    params = {
      ...params,
      templateId,
      linksetId,
      overlayId,
    };
  });
  const response = await axios
    .get(`${apiExpress}share/quickshare/${videoId}`, {
      headers: options.bearerToken,
      params,
    })
    .catch(err => err);
  if (response instanceof Error) {
    throw new Error('Error retrieving quick share link!');
  }
  const { data }: any = response;
  if (data.code !== 200) {
    throw new Error('Error retrieving quick share link!');
  }
  return data.data;
};

const fetchExpressData = async ({ dispatch, data, videoId }: FetchParams) => {
  dispatch(
    requestActionCreator({
      type: 'FETCH_REQUEST',
    })
  );
  const params = {
    templateId: data.templateId,
    linksetId: data.linksetId,
    overlayId: data.overlayId,
    iconId: data.iconId,
    vin: data.vin,
  };
  const res = await axios
    .get(`${apiExpress}share/quickshare/${videoId}`, {
      headers: options.bearerToken,
      params,
    })
    .catch(err => err);
  if (res instanceof Error) {
    dispatch(
      failureActionCreator({
        type: 'FETCH_FAILURE',
        payload: res.message,
      })
    );
    return;
  }
  const { url, html, key, legacyHtml } = res.data.data;
  dispatch(
    successActionCreator({
      type: 'FETCH_SUCCESS',
      payload: { url, html, key, legacyHtml },
    })
  );
  return { url, html, key };
};

export const useVideoShareLinkExpressStore = (): VideoShareLinkStore => {
  const initialState = {
    url: '',
    data: undefined,
    loading: false,
    error: null,
    isValid: false,
    videoId: undefined,
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { data, error, loading, isValid, videoId, url } = state;

  useActionListener(dispatch);

  useEffect(() => {
    if (isValid || !videoId || isNaN(data.templateId)) {
      return;
    }

    fetchExpressData({ dispatch, data, videoId });
  }, [videoId, isValid, data]);

  const setData = useCallback(
    obj => dispatch({ type: 'SET_DATA', payload: obj }),
    []
  );

  const invalidate = useCallback(() => dispatch({ type: 'INVALIDATE' }), []);

  return {
    url,
    data,
    loading,
    error,
    setData,
    invalidate,
  };
};
